/**
 * Compli access tiers -- the single frontend source for tier ids, display
 * labels and the capability matrix (Feature #24921).
 *
 * MIRRORS `backend/app/core/rbac/roles.py` (compli_basic / compli_quality /
 * compli_admin) and `_COMPLI_ROLE_TIERS` in `backend/app/api/routes/auth.py`.
 * The settings Compli Roles page, the impersonation banner and the
 * "view as role" route allow-list all derive from this module, so a tier
 * added or renamed here reaches every one of them.
 *
 * Pure module (no `next/headers`, no React): importable from client
 * components, route handlers and tests alike.
 */

import {
  COMPLI_CLASSIFY_FINDING,
  COMPLI_MANAGE_STANDARDS,
  COMPLI_REVISE_DOCUMENT,
  COMPLI_ROLES_ADMIN,
  expandRoles,
} from './roles'

export const COMPLI_TIER_IDS = ['compli_basic', 'compli_quality', 'compli_admin'] as const
export type CompliTierId = (typeof COMPLI_TIER_IDS)[number]

/** The pre-tier flat role. Still reported by the backend listing for a user
 * who holds it without any tier; never a valid assignment target. */
export const LEGACY_COMPLI_ROLE = 'compli'

/** Short label, as shown in the assignment table and its tier picker. */
export const COMPLI_TIER_LABELS: Readonly<Record<CompliTierId, string>> = {
  compli_basic: 'Basic',
  compli_quality: 'Quality',
  compli_admin: 'Admin',
}

export function isCompliTierId(value: unknown): value is CompliTierId {
  return typeof value === 'string' && (COMPLI_TIER_IDS as readonly string[]).includes(value)
}

/** Fully qualified label ("Compli Quality"), as shown in the preview banner
 * and on the preview buttons. Falls back to the raw value for an unknown role. */
export function compliTierFullLabel(role: string): string {
  return isCompliTierId(role) ? `Compli ${COMPLI_TIER_LABELS[role]}` : role
}

export interface CompliCapabilityRow {
  label: string
  /** Capability the row tests; null for the baseline every tier shares. */
  capability: string | null
}

/** One row per capability the tiers differ on, least to most privileged. */
export const COMPLI_CAPABILITY_ROWS: readonly CompliCapabilityRow[] = [
  { label: 'Run assessments & view findings', capability: null },
  { label: 'Classify / triage findings', capability: COMPLI_CLASSIFY_FINDING },
  { label: 'Revise documents', capability: COMPLI_REVISE_DOCUMENT },
  { label: 'Manage standards library', capability: COMPLI_MANAGE_STANDARDS },
  { label: 'Assign Compli roles', capability: COMPLI_ROLES_ADMIN },
]

/** Whether a tier grants a matrix row, read from the role bundles in `roles.ts`. */
export function tierGrantsRow(tier: CompliTierId, row: CompliCapabilityRow): boolean {
  return row.capability === null || expandRoles([tier]).has(row.capability)
}
